import { useMemo, useState } from "react";
import { useGetAllOrderQuery, useUpdateOrderMutation } from "../../services/orderApi";
import "./orders.css";
import { MdReceiptLong } from "react-icons/md";
import { formatDateTime } from "../../utils/format";
import { useNavigate } from "react-router-dom";
import {
  AdminPageHeader,
  AdminToolbar,
  AdminTable,
  AdminBadge,
  AdminButton,
  ORDER_STATUS_VARIANT,
  useConfirm,
  useToast,
} from "../../components/ui";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "amount_desc", label: "Total Amount (High-Low)" },
  { value: "amount_asc", label: "Total Amount (Low-High)" },
  { value: "status", label: "Order Status (A-Z)" },
];

function sortOrders(list, sortBy) {
  const sorted = list.slice();
  switch (sortBy) {
    case "oldest":
      return sorted.sort((a, b) => a.order_Id - b.order_Id);
    case "amount_desc":
      return sorted.sort((a, b) => (b.total_Amount ?? 0) - (a.total_Amount ?? 0));
    case "amount_asc":
      return sorted.sort((a, b) => (a.total_Amount ?? 0) - (b.total_Amount ?? 0));
    case "status":
      return sorted.sort((a, b) => (a.order_Status || "").localeCompare(b.order_Status || ""));
    case "newest":
    default:
      return sorted.sort((a, b) => b.order_Id - a.order_Id);
  }
}

function Orders() {
  const navigate = useNavigate();
  const response = useGetAllOrderQuery();
  const [updateOrder] = useUpdateOrderMutation();
  const confirm = useConfirm();
  const toast = useToast();
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const rows = useMemo(() => {
    const source = response.data || [];
    const term = search.trim().toLowerCase();
    const filtered = term
      ? source.filter((o) =>
          String(o.order_Id).includes(term) ||
          String(o.customer).includes(term) ||
          (o.order_Status && o.order_Status.toLowerCase().includes(term)) ||
          (o.address && o.address.city && o.address.city.toLowerCase().includes(term))
        )
      : source;
    return sortOrders(filtered, sortBy);
  }, [response.data, search, sortBy]);

  const handleCancel = async (order) => {
    const ok = await confirm({
      title: "Cancel order",
      message: `Cancel order #${order.order_Id}? This can't be undone.`,
      confirmLabel: "Cancel order",
      danger: true,
    });
    if (!ok) return;
    const res = await updateOrder({ id: order.order_Id, order_Status: "Canceled" });
    if (res.error) toast.error("Could not cancel this order.");
    else toast.success(`Order #${order.order_Id} canceled.`);
  };

  const columns = [
    {
      key: "order_Id",
      header: "Order",
      render: (o) => (
        <div className="orderRow">
          <span className="orderRow__icon"><MdReceiptLong /></span>
          <span className="orderRow__id">#{o.order_Id}</span>
        </div>
      ),
    },
    {
      key: "date",
      header: "Placed",
      render: (o) => formatDateTime(o.order_Placment_Date, o.order_Placment_Time),
    },
    { key: "total_Amount", header: "Total", align: "right", render: (o) => `₹${Number(o.total_Amount).toFixed(2)}` },
    {
      key: "address",
      header: "City",
      render: (o) => o.address?.city || "—",
    },
    {
      key: "order_Status",
      header: "Status",
      render: (o) => <AdminBadge variant={ORDER_STATUS_VARIANT[o.order_Status] || "neutral"}>{o.order_Status}</AdminBadge>,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (o) => (
        <div className="orderRow__actions">
          <AdminButton size="sm" variant="secondary" onClick={() => navigate(`/admin/order/details/${o.order_Id}`)}>
            Details
          </AdminButton>
          {o.order_Status !== "Canceled" && o.order_Status !== "Deliverd" && (
            <AdminButton size="sm" variant="danger" onClick={() => handleCancel(o)}>
              Cancel
            </AdminButton>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Orders"
        subtitle="Track and manage customer orders."
        breadcrumbs={[{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Orders" }]}
      />
      <AdminToolbar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search orders by ID, customer, status or city..."
        filters={
          <select className="admin-field__control" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        }
      />
      <AdminTable
        columns={columns}
        rows={rows}
        rowKey={(o) => o.order_Id}
        isLoading={response.isLoading}
        emptyIcon={<MdReceiptLong />}
        emptyTitle={search ? "No orders match your search" : "No orders yet"}
        pageSize={10}
      />
    </div>
  );
}
export default Orders;
